"use client";
import { Button } from "@/components/ui/button";

import {
  API_ENDPOINTS,
  GenericObjectInterface,
  headersList,
  makeApiRequest,
} from "@/src/utilities";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, useState, useEffect } from "react";
import { ArticleCard } from "../custom/BlogArticles/ArticleCard";
import { useQuery } from "@tanstack/react-query";
import { ITEMS_PER_PAGE } from "@/src/utilities/constants/constants";
import { PaginationButtons } from "../custom/PaginationButtons";

import SkeletonLoader from "../custom/Skeleton";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import { AppDispatch, RootState } from "@/src/store";
import { Coffee } from "lucide-react";
import { fetchSoftwareCategories } from "@/src/store/reducers/ungatedContent/ungatedContentThunk";
import AIDisclaimer from "../custom/AIDisclaimer";

import ResourceSectionWrapper from "./wrapper/ResourceSectionWrapper";

/**
 * @description This component is used to display the blog section/thought-leadership section
 * pagination is handled by the api
 * @returns {React.FC}
 */
const BlogSection = () => {
  const router = useRouter();
  const pathname = usePathname();
  const isBlogPage = pathname.includes("/blog");
  const [page, setPage] = useState(1);

  const dispatch = useAppDispatch<AppDispatch>();

  // Get blog filters and categories loading state from Redux
  const { selectedBlogCategory, selectedBlogSubcategory } = useAppSelector(
    (state: RootState) =>
      state.unGatedContentStateReducer.blogFilters || {
        selectedBlogCategory: null,
        selectedBlogSubcategory: null,
      },
  );

  const {
    categoriesLoading,
    categoriesError,
    productCategories,
    categoriesInitialized,
  } = useAppSelector((state: RootState) => state.unGatedContentStateReducer);

  console.log(
    "CATEGORIES LOADING, SELECTED SUBCATEGORY:",
    categoriesLoading,
    " ... ",
    selectedBlogSubcategory,
  );

  // Fetch categories on mount with proper dependency
  useEffect(() => {
    console.log("[BlogsSection] Categories fetch effect:", {
      productCategoriesLength: productCategories?.length,
      shouldFetch: productCategories.length === 0,
    });
    if (productCategories.length === 0) {
      console.log("[BlogsSection] Dispatching fetchSoftwareCategories()");
      dispatch(fetchSoftwareCategories());
    }
  }, [dispatch, productCategories?.length]);

  // Log when component renders and dependency values
  console.log("[BlogsSection] Component render with dependencies:", {
    page,
    selectedBlogSubcategory,
    selectedBlogCategory,
    categoriesLoading,
    categoriesInitialized,
    productCategoriesLength: productCategories?.length,
    enabledFlag:
      !!selectedBlogSubcategory && !categoriesLoading && categoriesInitialized,
  });

  const {
    data: blogDetails,
    isLoading,
    error: blogError,
  } = useQuery({
    queryKey: ["blogDetails", page, selectedBlogSubcategory],
    queryFn: async (): Promise<GenericObjectInterface> => {
      // Build query params based on selected filters
      let queryParams = `/${selectedBlogSubcategory}?pagination[page]=${page}&pagination[pageSize]=${ITEMS_PER_PAGE}`;

      const response = await makeApiRequest(
        "GET",
        `${API_ENDPOINTS.BLOGS_DETAILS_BY_SUBCATEGORY}${queryParams}`,
        null,
        headersList,
      );

      console.log("[BlogsSection] API response received:", response);
      return response?.data || [];
    },
    // Only enable query after categories are loaded and subcategory is set
    enabled:
      !!selectedBlogSubcategory && !categoriesLoading && categoriesInitialized,
    staleTime: 0, // Data is immediately stale - always fetch fresh data
    refetchOnMount: "always", // Always refetch when component mounts
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Log useQuery state after initialization
  console.log("[BlogsSection] useQuery state:", {
    isLoading,
    hasData: !!blogDetails,
    dataLength: blogDetails?.data?.length,
    hasError: !!blogError,
    errorMessage: blogError?.message,
  });

  const totalPages = useMemo(() => {
    return blogDetails?.meta?.pagination?.pageCount || 1;
  }, [blogDetails]);

  // Reset page to 1 when subcategory filter changes
  useEffect(() => {
    console.log("[BlogsSection] Subcategory changed effect triggered:", {
      selectedBlogSubcategory,
      previousPage: page,
    });
    setPage(1);
  }, [selectedBlogSubcategory]);

  // Show loading while categories are being fetched
  if (categoriesLoading) {
    console.log("[BlogsSection] Rendering skeleton - categories loading");
    return <SkeletonLoader />;
  }

  // Show error if categories failed to load
  if (categoriesError) {
    console.error("[BlogsSection] Categories error:", categoriesError);
    return (
      <section id="blog" className="bg-muted/80 py-24">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-center space-y-4">
              <h3 className="text-2xl font-semibold text-red-600">
                Failed to Load Categories
              </h3>
              <p className="text-gray-500 max-w-md">{categoriesError}</p>
              <Button
                variant="default"
                onClick={() => dispatch(fetchSoftwareCategories())}
                className="mt-5"
              >
                Retry
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error if blogs failed to load
  if (blogError) {
    console.error("[BlogsSection] Blog fetch error:", blogError);
  }
  return (
    <ResourceSectionWrapper
      sectionId="blog"
      title="Tech bytes and Insights"
      description="In-depth blogs, tech bytes and industry insights to help you make informed technology decisions."
      isLoading={isLoading}
      parentSection="article"
    >
      {/* Featured Posts */}
      <div className="mb-12 px-4 md:px-0">
        {blogDetails?.data?.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-6">
            {blogDetails?.data?.map(
              (post: GenericObjectInterface, index: number) => {
                return (
                  <ArticleCard
                    post={post}
                    index={index}
                    variant="featured"
                    key={index}
                  />
                );
              },
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="text-center space-y-4">
              <div className="w-full">
                <Coffee className="text-primary/70 mx-auto" size={48} />
              </div>
              <h3 className="text-2xl font-semibold text-gray-700">
                Brewing New Articles
              </h3>
              <p className="text-gray-500 max-w-md">
                We're currently crafting fresh content for this topic. Check
                back soon for insightful articles!
              </p>
            </div>
          </div>
        )}
      </div>
      {/* Pagination Controls */}
      {isBlogPage ? (
        <PaginationButtons
          totalPages={totalPages}
          currentPage={page}
          onPageChange={setPage}
          disabled={isLoading}
        />
      ) : null}
      {/* Load More */}
      {isBlogPage ? null : (
        <div className="flex justify-center">
          <Button
            variant="default"
            onClick={() => {
              router.push("/blog");
            }}
            className="mt-5"
          >
            Read More
          </Button>
        </div>
      )}
    </ResourceSectionWrapper>
  );
};

export default BlogSection;
